import React, { useState, useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import activoClassificationService from '../../../services/activoClassificationService';
import { useActivoClassification } from '../../../contexts/ActivoClassificationContext';
import FamilyForm from '../../../components/Classification/FamilyForm';
import ClassificationExcelToolbar from '../../../components/Classification/ClassificationExcelToolbar';
import {
  ClassificationBulkDeleteModal,
  ClassificationBulkDeleteTd,
  ClassificationBulkDeleteTh,
  ClassificationBulkDeleteToolbar,
} from '../../../components/Classification/ClassificationBulkDeleteUi';
import ConfirmModal from '../../../components/Common/ConfirmModal';
import Toast from '../../../components/Common/Toast';
import ActivosClassificationNav from './ActivosClassificationNav';
import { useCanMutate } from '../../../hooks/useCanMutate';
import { useClassificationBulkDelete } from '../../../hooks/useClassificationBulkDelete';
import { useClassificationHierarchyForBulk } from '../../../hooks/useClassificationHierarchyForBulk';

const ActivosFamilies = () => {
  const [searchParams] = useSearchParams();
  const subcategoryIdFromUrl = searchParams.get('subcategory');
  const categoryIdFromUrl = searchParams.get('category');
  const canMutate = useCanMutate();
  const { categories, subcategories, families, loading, error, refetch } = useActivoClassification();
  const { hierarchy: fullHierarchy, reloadHierarchy } = useClassificationHierarchyForBulk(
    activoClassificationService,
    families.length
  );
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(categoryIdFromUrl || '');
  const [selectedSubcategory, setSelectedSubcategory] = useState(subcategoryIdFromUrl || '');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingFamily, setEditingFamily] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [familyToDelete, setFamilyToDelete] = useState(null);
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });

  useEffect(() => {
    if (categoryIdFromUrl) setSelectedCategory(categoryIdFromUrl);
    if (subcategoryIdFromUrl) setSelectedSubcategory(subcategoryIdFromUrl);
  }, [categoryIdFromUrl, subcategoryIdFromUrl]);

  useEffect(() => {
    if (!subcategoryIdFromUrl || categoryIdFromUrl) return;
    const sub = subcategories.find((s) => String(s.id) === String(subcategoryIdFromUrl));
    if (sub) {
      const cid = String(sub.category_id ?? sub.activo_category_id ?? '');
      if (cid) setSelectedCategory(cid);
    }
  }, [subcategoryIdFromUrl, categoryIdFromUrl, subcategories]);

  const subcategoriesForCategory = useMemo(() => {
    if (!selectedCategory) return subcategories;
    return subcategories.filter(
      (s) => String(s.activo_category_id) === String(selectedCategory) || String(s.category_id) === String(selectedCategory)
    );
  }, [subcategories, selectedCategory]);

  const familiesFilteredBySelection = useMemo(() => {
    if (selectedSubcategory) {
      return families.filter((f) => String(f.subcategory?.id) === String(selectedSubcategory));
    }
    if (selectedCategory) {
      return families.filter(
        (f) =>
          String(f.subcategory?.activo_category_id) === String(selectedCategory) ||
          String(f.subcategory?.category_id) === String(selectedCategory)
      );
    }
    return families;
  }, [families, selectedCategory, selectedSubcategory]);

  const filteredFamilies = useMemo(
    () =>
      familiesFilteredBySelection.filter(
        (f) =>
          f.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          f.code.toLowerCase().includes(searchTerm.toLowerCase())
      ),
    [familiesFilteredBySelection, searchTerm]
  );

  const bulk = useClassificationBulkDelete({
    variant: 'families',
    rows: filteredFamilies,
    hierarchy: fullHierarchy,
    deleteItem: (id) => activoClassificationService.deleteFamily(id),
    onDeleted: async () => {
      await refetch();
      await reloadHierarchy();
    },
    setToast,
    enabled: canMutate,
  });

  if (loading) {
    return (
      <div className="p-6 flex justify-center items-center h-64">
        <div className="text-lg text-gray-600">Cargando familias…</div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:justify-between lg:items-start mb-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-800">Familias (activos)</h1>
              <p className="text-gray-600 mt-1">Nivel 3 — agrupa activos dentro de la subcategoría</p>
              {error && <p className="text-sm text-red-600 mt-1">{error}</p>}
            </div>
            <div className="flex flex-col items-stretch gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-end">
              <ClassificationExcelToolbar
                variant="families"
                onImported={refetch}
                setToast={setToast}
                apiService={activoClassificationService}
                filenameSuffix="activos"
                importTitleSuffix=" (activos)"
                exportRows={filteredFamilies}
                exportContext={{ categories, subcategories }}
              />
              <button
                type="button"
                onClick={() => {
                  setEditingFamily(null);
                  setIsFormOpen(true);
                }}
                className="px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-md font-medium"
              >
                + Nueva familia
              </button>
            </div>
          </div>

          <nav className="text-sm text-gray-600 mb-4">
            <Link to="/dashboard" className="hover:text-teal-600">Dashboard</Link>
            <span className="mx-2">/</span>
            <Link to="/activos" className="hover:text-teal-600">Activos</Link>
            <span className="mx-2">/</span>
            <span className="text-gray-800">Configuración</span>
            <span className="mx-2">/</span>
            <span className="text-gray-800">Clasificación</span>
            <span className="mx-2">/</span>
            <span className="text-gray-800">Familias</span>
          </nav>

          <ActivosClassificationNav />

          <div className="flex flex-nowrap items-end gap-3 mb-4 overflow-x-auto">
            <div className="flex flex-col gap-1 flex-shrink-0 w-44">
              <label className="text-xs text-gray-600">Categoría</label>
              <select
                value={selectedCategory}
                onChange={(e) => {
                  setSelectedCategory(e.target.value);
                  setSelectedSubcategory('');
                }}
                className="w-full px-3 py-2 border rounded-md text-sm"
              >
                <option value="">Todas</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>{c.code} — {c.name}</option>
                ))}
              </select>
            </div>
            <div className="flex flex-col gap-1 flex-shrink-0 w-56">
              <label className="text-xs text-gray-600">Subcategoría</label>
              <select
                value={selectedSubcategory}
                onChange={(e) => setSelectedSubcategory(e.target.value)}
                className="w-full px-3 py-2 border rounded-md text-sm"
              >
                <option value="">{selectedCategory ? 'Todas (esta categoría)' : 'Todas'}</option>
                {(selectedCategory ? subcategoriesForCategory : subcategories).map((s) => (
                  <option key={s.id} value={s.id}>{s.code} — {s.name}</option>
                ))}
              </select>
            </div>
            <div className="flex flex-col gap-1 flex-1 min-w-[12rem]">
              <label className="text-xs text-gray-600">Buscar</label>
              <input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-3 py-2 border rounded-md text-sm"
                placeholder="Buscar familias…"
              />
            </div>
          </div>
        </div>

        <ClassificationBulkDeleteToolbar bulk={bulk} />

        <div className="bg-white rounded-lg shadow overflow-x-auto">
          {filteredFamilies.length === 0 ? (
            <div className="p-8 text-center text-gray-500">No hay familias con los filtros actuales</div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <ClassificationBulkDeleteTh bulk={bulk} />
                  <th className="px-6 py-3 text-left text-xs text-gray-500 uppercase">Código</th>
                  <th className="px-6 py-3 text-left text-xs text-gray-500 uppercase">Nombre</th>
                  <th className="px-6 py-3 text-left text-xs text-gray-500 uppercase">Subcategoría</th>
                  <th className="px-6 py-3 text-left text-xs text-gray-500 uppercase">Estado</th>
                  <th className="px-6 py-3 text-left text-xs text-gray-500 uppercase">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {filteredFamilies.map((fam) => (
                  <tr key={fam.id} className="hover:bg-gray-50">
                    <ClassificationBulkDeleteTd bulk={bulk} row={fam} />
                    <td className="px-6 py-3 text-sm font-mono">{fam.code}</td>
                    <td className="px-6 py-3 text-sm">{fam.name}</td>
                    <td className="px-6 py-3 text-sm text-teal-800">
                      {fam.subcategory ? `${fam.subcategory.code} — ${fam.subcategory.name}` : '—'}
                    </td>
                    <td className="px-6 py-3 text-sm">
                      {fam.is_active ? (
                        <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">Activo</span>
                      ) : (
                        <span className="px-2 py-1 text-xs rounded-full bg-red-100 text-red-800">Inactivo</span>
                      )}
                    </td>
                    <td className="px-6 py-3 text-sm font-medium">
                      <button
                        type="button"
                        onClick={() => {
                          setEditingFamily(fam);
                          setIsFormOpen(true);
                        }}
                        className="text-purple-600 hover:text-purple-900 mr-3"
                      >
                        Editar
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setFamilyToDelete(fam);
                          setIsDeleteModalOpen(true);
                        }}
                        className="text-red-600 hover:text-red-900"
                      >
                        Eliminar
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <FamilyForm
        isOpen={isFormOpen}
        onClose={() => {
          setIsFormOpen(false);
          setEditingFamily(null);
        }}
        family={editingFamily}
        onSuccess={() => refetch()}
        apiService={activoClassificationService}
        preloadedSubcategories={subcategories}
      />

      <ClassificationBulkDeleteModal bulk={bulk} />

      <ConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setFamilyToDelete(null);
        }}
        onConfirm={async () => {
          if (!familyToDelete) return;
          try {
            const response = await activoClassificationService.deleteFamily(familyToDelete.id);
            if (response.success) {
              setIsDeleteModalOpen(false);
              setFamilyToDelete(null);
              setToast({ isOpen: true, message: 'Familia eliminada', type: 'success' });
              await refetch();
            } else {
              setToast({ isOpen: true, message: response.message || 'Error', type: 'error' });
            }
          } catch (e) {
            setToast({ isOpen: true, message: e.response?.data?.message || 'Error al eliminar', type: 'error' });
          }
        }}
        title="Eliminar familia"
        message={familyToDelete ? `¿Eliminar "${familyToDelete.name}" (${familyToDelete.code})?` : ''}
        confirmText="Eliminar"
        cancelText="Cancelar"
        type="danger"
      />

      <Toast
        isOpen={toast.isOpen}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ ...toast, isOpen: false })}
      />
    </div>
  );
};

export default ActivosFamilies;
